
import { supabase } from '@/integrations/supabase/client';
import { useNotify } from '@/hooks/useNotify';
import { useAuth } from '@/contexts/useAuth';
import { AdminUser } from '@/lib/types';


export const useUserActions = () => {
  const notify = useNotify();
  const { impersonateUser, user } = useAuth();

  const handleStatusToggle = async (
    userId: string, 
    currentIsActive: boolean, 
    setUsers: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    try {
      const newStatus = currentIsActive ? 'suspended' : 'active';

      if (newStatus === 'suspended') {
        // Use suspend_member RPC to enforce
        // last-admin and company suspension checks
        const { data: profileData } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', userId)
          .single();

        if (!profileData?.company_id) {
          throw new Error('Cannot find user company information');
        }

        const { error } = await supabase
          .rpc('suspend_member' as any, {
            p_user_id: userId,
            p_company_id: profileData.company_id,
            p_reason: 'Suspended by superuser',
            p_suspended_until: null
          } as any);

        if (error) throw error;
      } else {
        // Check if user was deactivated in company_members
        // If so, use reactivate_company_member RPC to
        // update both tables atomically
        const { data: profileData } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', userId)
          .single();

        const { data: memberData } = profileData?.company_id
          ? await supabase
              .from('company_members')
              .select('status')
              .eq('user_id', userId)
              .eq('company_id', profileData.company_id)
              .single()
          : { data: null };

        if (memberData?.status === 'deactivated') {
          // Use RPC to update both tables
          const { error } = await supabase
            .rpc('reactivate_company_member' as any, {
              p_user_id: userId,
              p_company_id: profileData.company_id
            } as any);

          if (error) throw error;
        } else {
          // Suspended user — just update profiles
          const { error } = await supabase
            .from('profiles')
            .update({
              status: newStatus,
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);

          if (error) throw error;
        }
      }

      setUsers(prev => prev.map(u => 
        u.id === userId 
          ? { 
              ...u, 
              is_active: !currentIsActive,
              status: newStatus
            } 
          : u
      ));

      notify.success(`User has been ${!currentIsActive ? 'activated' : 'suspended'}`);
    } catch (error: any) {
      console.error('Error updating user status:', error);
      if (
        error?.message?.includes('company is suspended') ||
        error?.message?.includes('Unsuspend the company first')
      ) {
        notify.error(
          'Cannot activate this user — their company is currently suspended. Unsuspend the company first.'
        );
      } else if (
        error?.message?.includes('last active admin') ||
        error?.message?.includes('Promote another member')
      ) {
        notify.error(error.message);
      } else {
        notify.error(error);
      }
    }
  };

  const handleMakeAdmin = async (
    userId: string,
    setUsers: (fn: (users: AdminUser[]) => AdminUser[]) => void
  ) => {
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .single();

      if (!profileData?.company_id) {
        throw new Error('Cannot find user company information');
      }

      const { error } = await supabase
        .rpc('grant_admin_role' as any, {
          p_user_id: userId,
          p_company_id: profileData.company_id
        } as any);

      if (error) throw error;

      setUsers(users => users.map(user =>
        user.id === userId ? { ...user, role: 'admin' } : user
      ));

      notify.success('User has been made an admin');
    } catch (error: any) {
      console.error('Error updating user role:', error);
      notify.error(error?.message || 'Failed to make user admin');
    }
  };

  const handleImpersonate = async (userId: string) => {
    try {
      await impersonateUser(userId);
      notify.success("You are now viewing as this user");
    } catch (error) {
      console.error('Error impersonating user:', error);
      notify.error(error);
    }
  };

  const handleInviteSuperuser = async (email: string, inviterName: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: invitation, error } = await supabase
        .from('superuser_invitations')
        .insert({
          email: email.toLowerCase().trim(),
          invited_by: user?.id
        })
        .select('id, token')
        .single();

      if (error) throw error;

      const { error: emailError } = await supabase.functions.invoke(
        'send-invitation',
        {
          body: {
            email: email.toLowerCase().trim(),
            companyName: 'Doyence Platform',
            inviterName: inviterName,
            invitationId: invitation.id,
            token: invitation.token
          }
        }
      );

      if (emailError) throw emailError;
    } catch (error) {
      console.error('Error inviting superuser:', error);
      throw error;
    }
  };

  const handleUpdateUser = async (
    userId: string,
    updates: {
      first_name: string;
      last_name: string;
      company_position: string;
      personal_phone_number: string;
    },
    setUsers: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: updates.first_name,
          last_name: updates.last_name,
          company_position: updates.company_position,
          personal_phone_number: updates.personal_phone_number,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;

      setUsers(prev => prev.map(u =>
        u.id === userId
          ? {
              ...u,
              company_position: updates.company_position,
              personal_phone_number: updates.personal_phone_number,
              profiles: {
                ...u.profiles,
                first_name: updates.first_name,
                last_name: updates.last_name,
                company_position: updates.company_position,
                personal_phone_number: updates.personal_phone_number
              }
            }
          : u
      ));

      notify.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      notify.error(error);
      throw error;
    }
  };

  return {
    handleStatusToggle,
    handleMakeAdmin,
    handleImpersonate,
    handleInviteSuperuser,
    handleUpdateUser
  };
};
